import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { createAdvertisementPlay } from "../../store/actions/advertisementsActions";
import {
    MIN_REPORTED_MS,
    adKey,
    displayMs,
    isCounted,
    isImage,
    loadCursor,
    saveCursor,
    startIndex,
} from "./adPlaylist";

// Проигрыватель рекламы — общий для заставки (variant="fullscreen") и экрана
// печати (variant="card").
//
// - Начинает с ролика после последнего засчитанного в этом месте показа
//   (справедливая очередь, см. adPlaylist.js).
// - Фото висит display_seconds, видео идёт до конца. Засчитанный показ
//   двигает курсор и пишется в статистику; прерванный (ушли с экрана раньше
//   порога) пишется как completed=false и курсор не двигает — в следующий
//   раз этот ролик начнётся заново.
// - maxPlays: сколько показов подряд, затем onFinished (круги заставки).
// - Битый файл пропускается; если не играет ничего — onFinished, чтобы
//   экран не висел пустым.
// - onPlayEnded — после каждого доигранного (не битого) показа.
//   Задержкой кнопки «Назад» экран печати управляет независимо от плеера.

const fade = {
    initial: { opacity: 0, scale: 1.02 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.5, ease: "easeIn" } },
};

function AdPlayer({ items, placement, variant = "card", maxPlays = Infinity, onFinished, onPlayEnded, className = "" }) {
    const dispatch = useDispatch();
    const [currentKey, setCurrentKey] = useState(() =>
        items.length ? adKey(items[startIndex(items, loadCursor(placement))]) : null,
    );
    // Номер показа: меняется и когда в очереди один ролик — тогда он играет
    // заново, а не замирает на последнем кадре.
    const [playId, setPlayId] = useState(0);
    const [progress, setProgress] = useState(0);

    const startedAtRef = useRef(Date.now());
    const watchedRef = useRef(0);
    const reportedRef = useRef(false);
    const playsRef = useRef(0);
    const errorsRef = useRef(0);
    const videoRef = useRef(null);
    const finishedRef = useRef(false);

    const index = useMemo(() => {
        const at = items.findIndex((ad) => adKey(ad) === currentKey);
        return at === -1 ? startIndex(items, loadCursor(placement)) : at;
    }, [items, currentKey, placement]);
    const current = items[index] || null;

    // Список поменялся, а текущего ролика в нём больше нет (сняли в панели) —
    // переходим на следующий по очереди.
    useEffect(() => {
        if (current && adKey(current) !== currentKey) setCurrentKey(adKey(current));
    }, [current, currentKey]);

    const report = useCallback(
        (ad, completed, watchedMs) => {
            const deviceToken = localStorage.getItem("deviceToken");
            if (!ad || !deviceToken) return;
            dispatch(
                createAdvertisementPlay({
                    advertisementId: ad.id,
                    deviceToken,
                    placement,
                    completed,
                    watchedMs,
                }),
            );
        },
        [dispatch, placement],
    );

    const watchedNow = useCallback(() => {
        if (!current) return 0;
        if (isImage(current)) return Date.now() - startedAtRef.current;
        return watchedRef.current;
    }, [current]);

    const finish = useCallback(() => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        onFinished?.();
    }, [onFinished]);

    const advance = useCallback(
        ({ ended = false, failed = false } = {}) => {
            if (!current || finishedRef.current) return;
            const watched = watchedNow();
            if (failed) {
                errorsRef.current += 1;
                if (errorsRef.current >= items.length) {
                    finish();
                    return;
                }
            } else {
                errorsRef.current = 0;
                const counted = isCounted(current, watched, {
                    ended,
                    durationMs: videoRef.current?.duration ? videoRef.current.duration * 1000 : null,
                });
                if (counted) saveCursor(placement, current);
                if (counted || watched >= MIN_REPORTED_MS) report(current, counted, watched);
                onPlayEnded?.();
                playsRef.current += 1;
                if (playsRef.current >= maxPlays) {
                    reportedRef.current = true;
                    finish();
                    return;
                }
            }
            reportedRef.current = false;
            watchedRef.current = 0;
            startedAtRef.current = Date.now();
            setProgress(0);
            const next = items[(index + 1) % items.length];
            setCurrentKey(adKey(next));
            setPlayId((id) => id + 1);
        },
        [current, index, items, maxPlays, placement, report, watchedNow, finish, onPlayEnded],
    );

    // Фото: таймер на display_seconds. Полоска прогресса — CSS-анимацией,
    // без перерисовки компонента каждый кадр.
    useEffect(() => {
        if (!current || !isImage(current)) return undefined;
        startedAtRef.current = Date.now();
        const timer = setTimeout(() => advance(), displayMs(current));
        return () => clearTimeout(timer);
    }, [current, playId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Уход с экрана посреди показа: пишем прерванный показ, курсор не трогаем.
    const pendingRef = useRef({});
    pendingRef.current = { current, watchedNow, report };
    useEffect(
        () => () => {
            const { current: ad, watchedNow: getWatched, report: send } = pendingRef.current;
            if (!ad || reportedRef.current || finishedRef.current) return;
            const watched = getWatched();
            if (isCounted(ad, watched)) {
                saveCursor(placement, ad);
                send(ad, true, watched);
            } else if (watched >= MIN_REPORTED_MS) {
                send(ad, false, watched);
            }
        },
        [placement],
    );

    if (!current) return null;

    const image = isImage(current);
    const segments = items.length > 1 ? items : null;
    const card = variant === "card";

    return (
        <div
            data-testid="ad-player"
            data-placement={placement}
            data-ad-key={adKey(current)}
            className={`relative overflow-hidden bg-[#0B0E14] ${card ? "rounded-[3vh]" : ""} ${className}`}
        >
            <AnimatePresence initial={false}>
                <motion.div
                    key={`${adKey(current)}:${playId}`}
                    className="absolute inset-0"
                    variants={fade}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    {image ? (
                        <>
                            {/* Размытая копия заполняет поля, когда пропорции
                                фото не совпадают с карточкой */}
                            <img
                                src={current.src}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 w-full h-full object-cover scale-110 blur-[4vh] opacity-60"
                            />
                            <img
                                src={current.src}
                                alt={current.name || ""}
                                className="relative w-full h-full object-contain"
                                onError={() => advance({ failed: true })}
                            />
                        </>
                    ) : (
                        <>
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#1E2533_0%,#0B0E14_75%)]" />
                            <video
                                ref={videoRef}
                                src={current.src}
                                className="relative w-full h-full object-contain"
                                autoPlay
                                muted
                                playsInline
                                preload="auto"
                                onTimeUpdate={(e) => {
                                    const v = e.currentTarget;
                                    watchedRef.current = Math.max(watchedRef.current, v.currentTime * 1000);
                                    if (v.duration) setProgress(v.currentTime / v.duration);
                                }}
                                onEnded={() => advance({ ended: true })}
                                onError={() => advance({ failed: true })}
                            />
                        </>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Лёгкое затемнение сверху — только чтобы полоски читались на светлом
                кадре. Метки «Реклама» нет: на экране она мешала самому ролику. */}
            <div className="absolute inset-x-0 top-0 h-[5vh] bg-gradient-to-b from-black/25 to-transparent pointer-events-none" />

            <div className={`absolute inset-x-0 top-0 flex items-center ${card ? "px-[2vw] pt-[1.6vh]" : "px-[3vw] pt-[2.4vh]"}`}>
                <div className="flex-1 flex gap-[0.6vw]">
                    {(segments || [current]).map((ad, i) => {
                        const state = i < index ? "done" : i === index ? "live" : "todo";
                        return (
                            <div key={adKey(ad)} className="flex-1 h-[0.5vh] rounded-full bg-white/30 overflow-hidden">
                                {state === "done" && <div className="h-full w-full bg-white" />}
                                {state === "live" &&
                                    (image ? (
                                        <div
                                            key={playId}
                                            className="h-full bg-white"
                                            style={{ animation: `adProgress ${displayMs(current)}ms linear forwards` }}
                                        />
                                    ) : (
                                        <div
                                            className="h-full bg-white transition-[width] duration-300 ease-linear"
                                            style={{ width: `${Math.round(progress * 100)}%` }}
                                        />
                                    ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default AdPlayer;
